/* eslint-disable react-refresh/only-export-components */

import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import type maplibregl from "maplibre-gl";

interface MapContextValue {
  flyTo: (lngLat: [number, number], zoom?: number) => void;
  map: maplibregl.Map | null;
  registerMap: (map: maplibregl.Map | null) => void;
  mapImagesLoaded: boolean;
  setMapImagesLoaded: (loaded: boolean) => void;
}

const MapContext = createContext<MapContextValue | null>(null);

export function MapProvider({ children }: { children: React.ReactNode }) {
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const [mapImagesLoaded, setMapImagesLoaded] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);

  const registerMap = useCallback((map: maplibregl.Map | null) => {
    mapRef.current = map;
    setMapInstance(map);
  }, []);

  const flyTo = useCallback((lngLat: [number, number], zoom?: number) => {
    const map = mapRef.current;
    if (!map) return;
    try {
      const currentZoom = map.getZoom();
      const targetZoom = zoom ?? Math.max(currentZoom, 12); // Default to 12 or current zoom, whichever is higher
      map.flyTo({
        center: lngLat,
        zoom: targetZoom,
        duration: 1000, // 1 second animation
        essential: true,
      });
    } catch (err) {
      // Map might not be fully loaded, silently ignore
      console.warn("Failed to fly to waypoint:", err);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  // Only recreate when actual values change
  const value = useMemo<MapContextValue>(
    () => ({
      flyTo,
      map: mapInstance,
      registerMap,
      mapImagesLoaded,
      setMapImagesLoaded,
    }),
    [flyTo, mapInstance, mapImagesLoaded, registerMap, setMapImagesLoaded]
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

export function useMap() {
  const ctx = useContext(MapContext);
  if (!ctx) {
    throw new Error("useMap must be used within MapProvider");
  }
  return ctx;
}
